//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { ReactNode, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { crl8 } from 'app/curalate.globals';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const REACT_QUERY_DEVTOOLS_SELECTOR = '.ReactQueryDevtools';
const REACT_QUERY_DEVTOOLS_OPEN_STATE_KEY = 'reactQueryDevtoolsOpen';

const bodyEl = document.querySelector('body');
const ReactQueryDevtoolsProduction = React.lazy(() =>
  import('@tanstack/react-query-devtools/build/lib/index.prod.js').then(
    (d) => ({
      default: d.ReactQueryDevtools,
    }),
  ),
);

export function ReactQueryProvider({ children }: { children: ReactNode }) {
  const [shouldRenderDevtools, setShouldRenderDevtools] = useState(false);

  useLayoutEffect(() => {
    crl8.toggleQueryDevtools =
      crl8.toggleQueryDevtools ??
      function () {
        const existingDevtools = document.querySelector(
          REACT_QUERY_DEVTOOLS_SELECTOR,
        );

        if (!existingDevtools && !crl8.queryDevtoolsAlreadyEnabled) {
          crl8.queryDevtoolsAlreadyEnabled = true;
          setShouldRenderDevtools(true);
        } else {
          crl8.queryDevtoolsAlreadyEnabled = false;
          setShouldRenderDevtools(false);
        }
      };

    const existingDevtools = document.querySelector(
      REACT_QUERY_DEVTOOLS_SELECTOR,
    );

    // On initial render we want to reopen query devtools if they were
    // open last time rendered. ReactQueryDevtools sets REACT_QUERY_DEVTOOLS_OPEN_STATE_KEY
    // to local storage when opening and closing
    if (
      !existingDevtools &&
      !crl8.queryDevtoolsAlreadyEnabled &&
      localStorage.getItem(REACT_QUERY_DEVTOOLS_OPEN_STATE_KEY) === 'true'
    ) {
      crl8.queryDevtoolsAlreadyEnabled = true;
      setShouldRenderDevtools(true);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {shouldRenderDevtools &&
        !!bodyEl &&
        createPortal(
          <React.Suspense fallback={null}>
            <ReactQueryDevtoolsProduction
              initialIsOpen={false}
              position='bottom-left'
            />
          </React.Suspense>,
          bodyEl,
        )}
    </QueryClientProvider>
  );
}
